import {
  KeyboardEvent,
  MouseEvent,
  PointerEvent,
  useMemo,
  useRef,
  useState
} from "react";
import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole
} from "@floating-ui/react";
import { Show } from "shared/components";
import { cx } from "shared/utils";

export type NoShowReasonSegment = {
  key: string;
  label: string;
  count: number;
  swatchClassName: string;
};

const nearestSegmentIndex = (segments: HTMLElement[], clientX: number) => {
  let best = -1;
  let bestDistance = Number.POSITIVE_INFINITY;
  segments.forEach((segment, index) => {
    const { left, right } = segment.getBoundingClientRect();
    const distance = Math.max(left - clientX, clientX - right, 0);
    if (distance < bestDistance) {
      bestDistance = distance;
      best = index;
    }
  });
  return best;
};

const formatPercent = (count: number, total: number) =>
  `${Math.round((count / total) * 100)}%`;

export const NoShowReasonBar = ({
  segments
}: {
  segments: NoShowReasonSegment[];
}) => {
  const visible = useMemo(
    () => segments.filter((segment) => segment.count > 0),
    [segments]
  );
  const total = visible.reduce((sum, segment) => sum + segment.count, 0);
  const isEmpty = total === 0;

  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const segmentRefs = useRef<HTMLElement[]>([]);

  const close = () => {
    setOpen(false);
    setPinned(false);
    setActiveIndex(-1);
  };

  const { refs, floatingStyles, context } = useFloating({
    open,
    placement: "bottom-end",
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift({ padding: 16 })],
    onOpenChange: (nextOpen, _event, reason) => {
      if (nextOpen) {
        setOpen(true);
        return;
      }
      if (pinned && (reason === "hover" || reason === "focus-out")) return;
      close();
    }
  });

  const hover = useHover(context, { enabled: !isEmpty, mouseOnly: true });
  const focus = useFocus(context, { enabled: !isEmpty });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role
  ]);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (pinned) {
      close();
      return;
    }
    setPinned(true);
    setOpen(true);
    setActiveIndex(nearestSegmentIndex(segmentRefs.current, event.clientX));
  };

  const handlePointerMove = (event: PointerEvent<HTMLButtonElement>) => {
    if (event.pointerType === "touch") return;
    setActiveIndex(nearestSegmentIndex(segmentRefs.current, event.clientX));
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== "ArrowRight" && event.key !== "ArrowLeft") return;
    event.preventDefault();
    setOpen(true);
    setActiveIndex((current) => {
      if (current < 0) return 0;
      const step = event.key === "ArrowRight" ? 1 : visible.length - 1;
      return (current + step) % visible.length;
    });
  };

  const ariaLabel = isEmpty
    ? "No no-shows in this range"
    : `${total} no shows. ${visible
        .map((segment) => `${segment.label}: ${segment.count}`)
        .join(". ")}`;
  const isHighlighting = open && activeIndex >= 0;
  segmentRefs.current.length = visible.length;

  return (
    <>
      <button
        type="button"
        ref={refs.setReference}
        disabled={isEmpty}
        aria-label={ariaLabel}
        className="group flex h-8 min-w-[72px] flex-1 items-center rounded-md disabled:cursor-default"
        {...getReferenceProps({
          onClick: handleClick,
          onPointerMove: handlePointerMove,
          onKeyDown: handleKeyDown
        })}
      >
        <span
          className={cx(
            "flex w-full gap-0.5 overflow-hidden rounded-full bg-alpha-80 transition-[height] duration-100 motion-reduce:transition-none",
            open ? "h-2.5" : "h-2 group-enabled:group-hover:h-2.5"
          )}
        >
          {visible.map((segment, index) => (
            <span
              key={segment.key}
              ref={(node) => {
                if (node) segmentRefs.current[index] = node;
              }}
              className={cx(
                "h-full min-w-1 transition-opacity duration-100 motion-reduce:transition-none",
                segment.swatchClassName,
                isHighlighting && index !== activeIndex && "opacity-30"
              )}
              style={{ flex: `${segment.count} 1 0` }}
            />
          ))}
        </span>
      </button>
      <Show if={open && !isEmpty}>
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className="pointer-events-none z-[70] w-max min-w-[280px] max-w-[calc(100vw-32px)] rounded-lg border border-alpha-100 bg-content-1000-inverted px-4 pb-2 pt-3 shadow-xl"
            {...getFloatingProps()}
          >
            <div className="mb-1 flex items-baseline gap-2 border-b border-alpha-80 pb-2 text-[13px] text-content-300">
              <b className="flex-1 text-sm font-bold text-content-1000">
                {total} {total === 1 ? "no show" : "no shows"}
              </b>
              <span>by reason</span>
            </div>
            {visible.map((segment, index) => (
              <div
                key={segment.key}
                className={cx(
                  "-mx-2 flex h-8 items-center gap-2 rounded-md px-2 text-sm transition-opacity duration-100 motion-reduce:transition-none",
                  index === activeIndex && "bg-alpha-40",
                  isHighlighting && index !== activeIndex && "opacity-60"
                )}
              >
                <span
                  className={cx(
                    "size-2 shrink-0 rounded-full",
                    segment.swatchClassName
                  )}
                />
                <span className="min-w-0 flex-1 truncate pr-4 text-content-1000">
                  {segment.label}
                </span>
                <span className="min-w-4 text-right font-bold text-content-1000">
                  {segment.count}
                </span>
                <span className="w-10 text-right text-[13px] text-content-300">
                  {formatPercent(segment.count, total)}
                </span>
              </div>
            ))}
          </div>
        </FloatingPortal>
      </Show>
    </>
  );
};
